import React from "react";
import { ThunkDispatch } from "redux-thunk";
import { connect } from "react-redux";
import { SearchBar } from "cm-component-library/lib/CMLayout/Navbar/";
import { appPaths, IAppState } from "../../../../Common";
import styles from "../../../../Layout/Styles/SearchBarContainer.module.scss";
import { UserAutocompleteItem, AutocompleteItem } from "../../../../Layout/Models/AutocompleteItem";
import {
  IGetUserAutocompleteResultsArguments,
  UserAutocompleteSearchAction,
  getAutocompleteResult,
  unloadSearch
} from "../../../Actions/UserAutocompleteSearchActions";
import AutocompleteResults from "../../../../Layout/Components/Autocomplete/AutocompleteResults";
import { AutocompleteItemType } from "../../../../Layout/Constants/LayoutEnums";

type Props = {
  placeholder: string;
  icon: React.ReactElement;
  onSelectUser: (user: UserAutocompleteItem) => void;
};

type StateProps = {
  items: UserAutocompleteItem[];
  isLoaded: boolean;
  isLoading: boolean;
  isErrored: boolean;
  errorMessage: string;
  errorCode?: number;
};

type DispatchProps = {
  getAutocompleteResult: (args: IGetUserAutocompleteResultsArguments) => void;
  unloadSearch: () => void;
};

type State = {
  searchQuery: string;
  selectedItemIndex: number;
};

function mapStateToProps(state: IAppState): StateProps {
  return {
    items: state.userAutocompleteSearch.items,
    isLoaded: state.userAutocompleteSearch.isLoaded,
    isLoading: state.userAutocompleteSearch.isLoading,
    isErrored: state.userAutocompleteSearch.isErrored,
    errorMessage: state.userAutocompleteSearch.errorMessage,
    errorCode: state.userAutocompleteSearch.errorCode
  };
}

function mapDispatchToProps(
  dispatch: ThunkDispatch<IAppState, {}, UserAutocompleteSearchAction>
): DispatchProps {
  return {
    getAutocompleteResult: (args: IGetUserAutocompleteResultsArguments) =>
      dispatch(getAutocompleteResult(args)),
    unloadSearch: () => dispatch(unloadSearch())
  };
}

class UserSearchBarContainer extends React.Component<Props & StateProps & DispatchProps, State> {
  constructor(props: Props & StateProps & DispatchProps) {
    super(props);

    this.state = {
      searchQuery: "",
      selectedItemIndex: -1
    };

    this.onSearchQueryChange = this.onSearchQueryChange.bind(this);
    this.onSearchKeyUp = this.onSearchKeyUp.bind(this);
    this.onSearchKeyDown = this.onSearchKeyDown.bind(this);
    this.clearAutocompleteIfLoaded = this.clearAutocompleteIfLoaded.bind(this);
    this.onAutocompleteItemClick = this.onAutocompleteItemClick.bind(this);
  }

  onSearchQueryChange(searchQuery: string) {
    this.setState(
      (prevState: State) => {
        return {
          ...prevState,
          searchQuery
        };
      },
      () => {
        // only load the autocomplete if we have entered at least 3 characters
        if (this.state.searchQuery.length > 2) {
          this.loadAutocompleteSuggestions();
        } else {
          this.clearAutocompleteIfLoaded();
        }
      }
    );
  }

  onSearchKeyUp(keyCode: number, event: any) {
    if (keyCode === 13) {
      if (this.state.selectedItemIndex > -1) {
        this.onAutocompleteItemClick(this.props.items[this.state.selectedItemIndex]);
      }
    } else if (keyCode === 38) {
      // up arrow key
      this.moveSelectedAutocompleteItemUp();
    } else if (keyCode === 40) {
      // down arrow key
      this.moveSelectedAutocompleteItemDown();
    }
  }

  onSearchKeyDown(keyCode: number, event: any) {
    if (keyCode === 38 || keyCode === 40) {
      // disable up and down arrow if there are autocomplete results
      if (this.props.items.length > 0) {
        event.preventDefault();
      }
    }
  }

  onAutocompleteItemClick(item: AutocompleteItem) {
    if (item.type === AutocompleteItemType.User) {
      const userItem = item as UserAutocompleteItem;
      this.onUserAutocompleteItemClick(userItem);
    }
  }

  onUserAutocompleteItemClick(item: UserAutocompleteItem) {
    this.props.onSelectUser(item);
    this.clearAutocompleteIfLoaded();
  }

  loadAutocompleteSuggestions() {
    this.setState(state => {
      return { ...state, selectedItemIndex: -1 };
    });
    this.props.getAutocompleteResult({ ...this.props, ...this.state, maxNumToGet: 5 });
  }

  clearAutocompleteIfLoaded() {
    if (this.props.items.length > 0) {
      this.setState(state => {
        return { ...state, selectedItemIndex: -1, redirectingToItemIndex: -1 };
      });
      this.props.unloadSearch();
    }
  }

  moveSelectedAutocompleteItemUp() {
    if (this.state.selectedItemIndex > -1) {
      this.setState(state => {
        return { ...state, selectedItemIndex: state.selectedItemIndex - 1 };
      });
    }
  }

  moveSelectedAutocompleteItemDown() {
    if (this.state.selectedItemIndex < this.props.items.length - 1) {
      this.setState(state => {
        return { ...state, selectedItemIndex: state.selectedItemIndex + 1 };
      });
    }
  }

  render() {
    return (
      <div className={styles.FeedbackSearchBarContainer}>
        <SearchBar
          placeholder={this.props.placeholder}
          // icon="" // TODO fix
          onSearchQueryChange={this.onSearchQueryChange}
          onSearchKeyUp={this.onSearchKeyUp}
          onSearchKeyDown={this.onSearchKeyDown}
          searchQuery={this.state.searchQuery}
        />

        <AutocompleteResults
          items={this.props.items}
          selectedItemIndex={this.state.selectedItemIndex}
          clearAutocompleteIfLoaded={this.clearAutocompleteIfLoaded}
          onItemClick={this.onAutocompleteItemClick}
        />
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserSearchBarContainer);
